// NewMenu.js
import React, { useRef, useEffect, useMemo, useState } from "react";
import {
  fetchMenu,
  fetchTypes,
  getLocalizedValue,
  getLangFromUrl,
} from "../api";
import "./MenuPage.css";

export default function NewMenu() {
  const lang = getLangFromUrl();
  const tabContainerRef = useRef(null);

  const [menuItems, setMenuItems] = useState([]);
  const [types, setTypes] = useState([]);
  const allLabel = lang === "PT" ? "Todos" : "All";
  const [selectedType, setSelectedType] = useState(allLabel);
  const [search] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // Scroll active tab into view
  useEffect(() => {
    const container = tabContainerRef.current;
    const activeTab = container?.querySelector(".tab.active");
    if (!container || !activeTab) return;

    const offset =
      activeTab.offsetLeft -
      container.offsetLeft -
      container.clientWidth / 2 +
      activeTab.clientWidth / 2;
    container.scrollTo({ left: offset, behavior: "smooth" });
  }, [selectedType, isMobile]);

  // Detect mobile
  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth <= 1000);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Fetch menu and types
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setLoadError("");

    Promise.all([fetchMenu({ limit: 100 }), fetchTypes()])
      .then(([menu, t]) => {
        if (!mounted) return;
        setMenuItems(menu || []);
        setTypes(t || []);
      })
      .catch((err) => {
        if (!mounted) return;
        setLoadError(err?.message || "Failed to load menu.");
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, []);

  // Filter menu items
  const filteredItems = useMemo(() => {
    const q = search.toLowerCase();

    const items =
      selectedType === allLabel
        ? menuItems
        : menuItems.filter(
            (i) =>
              getLocalizedValue(
                i?.type?.value?.names,
                lang,
                i?.type?.value?.displayName
              ) === selectedType
          );

    return items.filter((item) => {
      const title = getLocalizedValue(
        item?.names,
        lang,
        item?.title || ""
      ).toLowerCase();
      const ingMatch = Array.isArray(item?.ingredients)
        ? item.ingredients.some((ing) =>
            getLocalizedValue(ing?.names, lang, "").toLowerCase().includes(q)
          )
        : false;
      return title.includes(q) || ingMatch;
    });
  }, [menuItems, allLabel, selectedType, search, lang]);

  // Decide which types to render
  const typesToRender = useMemo(() => {
    if (selectedType === allLabel) return types;
    const selected = types.find(
      (t) => getLocalizedValue(t.names, lang, t.displayName) === selectedType
    );
    return selected ? [selected] : [];
  }, [types, allLabel, selectedType, lang]);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedType]);
  return (
    <div className="menu-page">
      {/* Tabs */}
      <div
        className={`menu-type-filter modern ${
          isMobile ? "mobile-tabs" : "desktop-tabs"
        }`}
        ref={tabContainerRef}
      >
        <button
          className={selectedType === allLabel ? "tab active" : "tab"}
          onClick={() => setSelectedType(allLabel)}
        >
          {allLabel}
        </button>
        {types.map((type) => {
          const typeName = getLocalizedValue(type.names, lang);
          return (
            <button
              key={type.id}
              className={selectedType === typeName ? "tab active" : "tab"}
              onClick={() => setSelectedType(typeName)}
            >
              {typeName}
            </button>
          );
        })}
      </div>

      {loading && <div className="menu-loading">Loading…</div>}
      {loadError && <div className="menu-error">{loadError}</div>}

      {/* Menu sections */}
      {!loading &&
        !loadError &&
        typesToRender.map((type) => {
          const typeName = getLocalizedValue(
            type.names,
            lang,
            type.displayName
          );
          const itemsOfType = filteredItems.filter(
            (item) => item?.type?.value?.id === type.id
          );
          if (!itemsOfType.length) return null;

          return (
            <section key={type.id} className="menu-type-section">
              <h2 className="menu-type-title">{typeName}</h2>
              <div className="menu-type-grid">
                {itemsOfType.map((item) => {
                  const title = getLocalizedValue(
                    item?.names,
                    lang,
                    item?.title || ""
                  );
                  const img =
                    item?.media?.sizes?.tablet?.url ||
                    item?.media?.url ||
                    "/pizza.png";

                  return (
                    <div className="menu-card modern" key={item.id}>
                      <div className="menu-card-img-wrap modern">
                        <img
                          src={img}
                          alt={title}
                          loading="lazy"
                          className="menu-card-img modern"
                        />
                      </div>
                      <div className="menu-card-body">
                        <h3 className="menu-card-title">{title}</h3>
                        {Array.isArray(item?.prices) && (
                          <div className="menu-card-prices">
                            {item.prices.map((p, idx) => (
                              <span
                                key={p.id || idx}
                                className="menu-card-price"
                              >
                                {p.price}
                              </span>
                            ))}
                          </div>
                        )}
                        {Array.isArray(item?.additionalInfo) && (
                          <ul className="menu-card-ingredients">
                            {item.additionalInfo.map((info) => {
                              const code = info.language?.value?.code;
                              if (code?.toLowerCase() !== lang.toLowerCase())
                                return null;
                              return <li key={info.id}>{info.value}</li>;
                            })}
                          </ul>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
    </div>
  );
}
